import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getAssetPath } from '../../utils/assetHelper';
import SButton from '../ui/SButton';
import { useUser } from '../../context/UserContext';
import { useAuth } from '../../context/AuthContext';
import './Header.scss';

const Header: React.FC = () => {
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const { userProfile, characters, isLoading } = useUser();
  const { logout } = useAuth();
  const currentCharacter = characters && characters.length > 0 ? characters[0] : null;
  const menuRef = useRef<HTMLDivElement>(null);
  
  const toggleProfileMenu = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling
    setProfileMenuOpen(prev => !prev);
  };

  const handleLogout = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      await logout();
      setProfileMenuOpen(false);
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Get avatar image based on character type
  const getAvatarImage = () => {
    if (!currentCharacter) return 'assets/images/avatar-1.png';
    
    switch (currentCharacter.type) {
      case 'Phantom':
        return 'assets/images/avatar-2.png';
      case 'Dark Cleric':
        return 'assets/images/character-2.png';
      default:
        return 'assets/images/avatar-1.png';
    }
  };
  
  return (
    <header className="l-header position-fixed">
      <div className="c-header container">
        {/* Logo */}
        <div className="c-header__logo">
          <Link to="/">
            <img src={getAssetPath('assets/images/logo-white.svg')} alt="logo" />
          </Link>
          <Link to="/" className="mobile">
            <img src={getAssetPath('assets/images/logo-icon.svg')} alt="logo" />
          </Link>
        </div>
        
        {/* Page Title */}
        <div className="c-header__pagetitle">
          <img src={getAssetPath('assets/images/dashboard-icon.svg')} alt="" />
          <span>Dashboard</span>
        </div>
        
        {/* Header Buttons */}
        <div className="header-buttons-container">
          {/* Valor Points Button */}
          <SButton 
            to="#"
            className="valor-btn"
            icon={<img 
              src={getAssetPath('assets/images/valor-points-icon@1x.png')} 
              srcSet={`${getAssetPath('assets/images/valor-points-icon@2x.png')} 2x`} 
              alt="" 
            />}
            label={userProfile?.valorPoints || 0}
            sublabel="Valor Points"
          />
          
          {/* Level Button */}
          <SButton 
            to="#"
            variant="lvl"
            className="level-btn"
            icon={currentCharacter?.level || 1}
            label={currentCharacter?.name || 'New Character'}
            sublabel="Level"
          />
          
          {/* Balance Button */}
          <SButton 
            to="#"
            variant="blnc"
            className="balance-btn"
            icon={<img 
              src={getAssetPath('assets/images/phan-icon@1x.png')} 
              srcSet={`${getAssetPath('assets/images/phan-icon@2x.png')} 2x`} 
              alt="" 
            />}
            label={<>{userProfile?.valorPoints || 0}<small></small></>}
            sublabel="$PHAN"
          />
        </div>
        
        {/* Notification Icon */}
        <div className="c-header__notify">
          <div className="c-header__notify-icon">
            <span></span>
            <i className="bi bi-bell-fill"></i>
          </div>
        </div>
        
        {/* Profile Menu */}
        <div className="c-header__profile" ref={menuRef}>
          <button 
            className={`c-header__profile-avatar ${profileMenuOpen ? 'active' : ''}`} 
            onClick={toggleProfileMenu}
            type="button"
            style={{ border: 'none', background: 'transparent' }}
          >
            <div className="c-header__profile-avatar-pic">
              <span></span>
              <img src={getAssetPath(getAvatarImage())} alt="Avatar" />
            </div>
            <i className="bi bi-chevron-down"></i>
          </button>
          
          <div className="c-header__profile-menu" style={{ display: profileMenuOpen ? 'block' : 'none' }}>
            <ul>
              <li>
                <Link to="/wallet-finance">
                  <i className="bi bi-wallet"></i>
                  Wallet
                  <span className="balance">
                    <img src={getAssetPath('assets/images/phan-icon@1x.png')} alt="" /> {userProfile?.valorPoints || 0}
                  </span>
                </Link>
              </li>
              <li>
                <Link to="/settings-profile">
                  <i className="bi bi-person-circle"></i> {userProfile?.username || 'My Profile'} <span className="status"></span>
                </Link>
              </li>
            </ul>
            <ul>
              <li>
                <Link to="/settings-notifications">
                  <i className="bi bi-bell"></i> Notifications Settings
                </Link>
              </li>
              <li>
                <Link to="/settings-security">
                  <i className="bi bi-lock"></i> Security Settings
                </Link>
              </li>
              <li>
                <Link to="/settings-privacy">
                  <i className="bi bi-eye"></i> Privacy Settings
                </Link>
              </li>
            </ul>
            <ul>
              <li>
                <Link to="/settings-display">
                  <i className="bi bi-window"></i> Theme & Display
                </Link>
              </li>
              <li>
                <Link to="/settings-advanced">
                  <i className="bi bi-layers"></i> Advanced Settings
                </Link>
              </li>
            </ul>
            <ul>
              <li>
                <Link to="#">
                  <i className="bi bi-vignette"></i> Change the hero
                </Link>
              </li>
            </ul>
            <ul>
              <li className="logout">
                <a href="#" onClick={handleLogout}>
                  <i className="bi bi-box-arrow-left"></i> Logout
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 